import React, { useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from '@mui/material';
import { UploadFile, Image } from '@mui/icons-material';
import { useHistory } from 'react-router-dom';

const ACCEPT = {
  all: '*',
  docs: '.pdf,.doc,.docx,.xls,.xlsx,.csv',
  images: '.png,.jpg,.jpeg,.svg',
};

const AddAttachment = () => {
  const history = useHistory();
  const inputRef = useRef(null);
  const [meta, setMeta] = useState({ stage: '', type: 'Document', tags: '' });
  const [uploaded, setUploaded] = useState([]);

  const stages = useMemo(() => ([
    'Lead Generation',
    'Site Survey',
    'Quotation',
    'Order Confirmed',
    'Installation Planning',
    'Installation',
    'Quality Check',
    'Commissioning',
    'Handover',
    'AMC / Maintenance',
  ]), []);

  const onBrowse = () => inputRef.current?.click();

  const addFiles = (list) => {
    setUploaded(prev => ([
      ...prev,
      ...list.map(f => ({
        id: Date.now() + Math.random(),
        name: f.name,
        size: f.size,
        type: meta.type,
        stage: meta.stage || 'General',
        tags: meta.tags,
        preview: f.type?.startsWith?.('image/'),
      }))
    ]));
  };

  const onUpload = (e) => addFiles(Array.from(e.target.files || []));
  const onDrop = (e) => { e.preventDefault(); addFiles(Array.from(e.dataTransfer.files || [])); };

  const handleSave = () => {
    // Here you would call API to persist files + meta
    console.log('Saving attachments:', { meta, uploaded });
    alert('Attachments saved (demo)');
    history.push('/app/green-energy/project/documents');
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
          Add Attachment
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Select stage and type, add tags, then upload documents or images
        </Typography>
      </Box>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} sx={{ mb: 1 }}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Stage</InputLabel>
                <Select value={meta.stage} label="Stage" onChange={(e) => setMeta(prev => ({ ...prev, stage: e.target.value }))}>
                  <MenuItem value="">General</MenuItem>
                  {stages.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select value={meta.type} label="Type" onChange={(e) => setMeta(prev => ({ ...prev, type: e.target.value }))}>
                  <MenuItem value="Document">Document</MenuItem>
                  <MenuItem value="Image">Image</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Tags (comma separated)" value={meta.tags} onChange={(e) => setMeta(prev => ({ ...prev, tags: e.target.value }))} />
            </Grid>
          </Grid>

          <Box
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            sx={{
              border: '2px dashed #c5cae9',
              borderRadius: 2,
              p: 3,
              backgroundColor: '#fafafa',
              textAlign: 'center',
            }}
          >
            <Image sx={{ color: '#536DFE', fontSize: 36 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 1 }}>Drag & drop files here</Typography>
            <Typography variant="caption" color="text.secondary">or</Typography>
            <Box sx={{ mt: 1 }}>
              <Button variant="contained" size="small" startIcon={<UploadFile />} onClick={onBrowse} sx={{ backgroundColor: '#536DFE', '&:hover': { backgroundColor: '#3F51B5' } }}>Browse Files</Button>
              <input hidden ref={inputRef} type="file" accept={`${ACCEPT.images},${ACCEPT.docs}`} multiple onChange={onUpload} />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Supported: Images (PNG/JPG/SVG) & Docs (PDF/DOC/XLS)
            </Typography>
          </Box>

          {meta.tags && (
            <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {meta.tags.split(',').filter(Boolean).map(t => (
                <Chip key={t.trim()} label={t.trim()} size="small" variant="outlined" />
              ))}
            </Box>
          )}

          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
            <Button variant="outlined" onClick={() => history.push('/app/green-energy/project/documents')}>Back</Button>
            <Button variant="contained" onClick={handleSave} sx={{ backgroundColor: '#536DFE', '&:hover': { backgroundColor: '#3F51B5' } }}>Save</Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AddAttachment;


